import { cn } from "@/lib/utils";

const COLORS = {
    success: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    info: "bg-blue-50 text-blue-700 ring-blue-200",
    warning: "bg-amber-50 text-amber-700 ring-amber-200",
    error: "bg-rose-50 text-rose-700 ring-rose-200",
    neutral: "bg-gray-100 text-gray-700 ring-gray-200",
    primary: "bg-primary/10 text-primary ring-primary/20",
};

const DOT = {
    success: "bg-emerald-500",
    info: "bg-blue-500",
    warning: "bg-amber-500",
    error: "bg-rose-500",
    neutral: "bg-gray-400",
    primary: "bg-primary",
};

export default function StatusPill({ tone = "neutral", children, className, withDot = true, testid }) {
    return (
        <span
            data-testid={testid}
            className={cn(
                "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ring-1 ring-inset",
                COLORS[tone] || COLORS.neutral,
                className,
            )}
        >
            {withDot && <span className={cn("h-1.5 w-1.5 rounded-full", DOT[tone] || DOT.neutral)} />}
            {children}
        </span>
    );
}

const STATUS_TONE = {
    draft: "neutral",
    scheduled: "info",
    running: "success",
    paused: "warning",
    completed: "primary",
    failed: "error",
};

export function CampaignStatus({ status }) {
    const tone = STATUS_TONE[status] || "neutral";
    return (
        <StatusPill tone={tone} testid={`campaign-status-${status}`}>
            <span className="capitalize">{status}</span>
        </StatusPill>
    );
}

// ─── Lead status ────────────────────────────────────────────────────────────
const LEAD_TONE = {
    new: "info",
    contacted: "primary",
    responded: "success",
    converted: "success",
    archived: "neutral",
    do_not_contact: "error",
};
export function LeadStatusPill({ status, className }) {
    const tone = LEAD_TONE[status] || "neutral";
    return (
        <StatusPill tone={tone} className={className} testid={`lead-status-${status}`}>
            <span className="capitalize">{(status || "—").replace(/_/g, " ")}</span>
        </StatusPill>
    );
}

// ─── Client status ──────────────────────────────────────────────────────────
const CLIENT_TONE = {
    active: "success",
    pending_docs: "warning",
    submitted: "info",
    monitoring: "primary",
    past_due: "warning",
    cancelled: "error",
    completed: "success",
    inactive: "neutral",
};
export function ClientStatusPill({ status, className }) {
    const tone = CLIENT_TONE[status] || "neutral";
    return (
        <StatusPill tone={tone} className={className} testid={`client-status-${status}`}>
            <span className="capitalize">{(status || "active").replace(/_/g, " ")}</span>
        </StatusPill>
    );
}

// ─── Alert severity ─────────────────────────────────────────────────────────
const SEV_TONE = { urgent: "error", warning: "warning", info: "info" };
export function AlertSeverityPill({ severity, className }) {
    const tone = SEV_TONE[severity] || "info";
    return (
        <StatusPill tone={tone} className={className} withDot={false} testid={`alert-severity-${severity}`}>
            <span className="uppercase tracking-wider text-[10px] font-semibold">{severity}</span>
        </StatusPill>
    );
}

// ─── Qualification result source ────────────────────────────────────────────
export function ResultSourcePill({ source, className }) {
    const tone = source === "calculated" ? "info" : "neutral";
    const label = source === "calculated" ? "Calculated" : "Manual";
    return (
        <StatusPill tone={tone} className={className} withDot={false} testid={`result-source-${source || "manual"}`}>
            <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
        </StatusPill>
    );
}

// ─── Qualification eligible status ──────────────────────────────────────────
const ELIG_TONE = {
    eligible: "success",
    estimated: "info",
    needs_review: "warning",
    not_eligible: "error",
    manual: "neutral",
};
export function EligibleStatusPill({ status, className }) {
    const tone = ELIG_TONE[status] || "neutral";
    return (
        <StatusPill tone={tone} className={className} withDot={false} testid={`elig-status-${status}`}>
            <span className="text-[10px] uppercase tracking-wider font-semibold">{(status || "—").replace(/_/g, " ")}</span>
        </StatusPill>
    );
}
